/*
 * One delivery date for every surface (Shai's redesign part 3, SPEC.md 3a, 2026-10-23; launchpad task
 * shopmvsport-redesign-p3-delivery-date-tile). The floating tile, the Studio, the group pages, the cart and the footer all
 * read the date from here, so they never disagree.
 *
 *   start   = today in America/New_York; after the daily cutoff (theme setting delivery_cutoff_hour, default 13 = 1 PM ET)
 *             or on a weekend or holiday, the next business day
 *   ships   = start + 2 business days (printing and shipping, Cal 2026-11-01)
 *   arrives = ships + USPS Ground Advantage transit days for the ZIP's zone
 *
 * The transit days come from the Studio AI Worker's GET /api/ship?zip= (its USPS zone chart from Bay Shore 11706 and
 * the transit days per zone that Shopify's carrier quote showed). The Worker has no cutoff, so before the cutoff on a
 * business day its arrivesBy equals ours; after the cutoff ours is one business day later, and every surface shows ours.
 * The holiday list is a copy of the Worker's (studio-ai/src/index.ts HOLIDAYS); keep the two the same.
 * A group order is printed the day after it closes (11:59 PM on its close day), so its start is the next business day.
 *
 * ZIP: the one the shopper typed (localStorage mv_zip, the same on every page), else the Worker's guess from the
 * visitor's location (not saved), else none: then the date is hidden and the surfaces ask for a ZIP.
 *
 * API: mvDelivery.get() → promise of {zip, typed, known, transitDays, arrives, start, ships, leftMin}; .setZip(zip, from);
 * .fromClose('YYYY-MM-DD') → promise of the same for a group's close day; .closeEstimate(ymd) the same, with the far end
 * of the "4 to 7 business days" promise when no ZIP is known; .paintClose(node, ymdFn, words) fills a group's
 * "Close by … → arrives by …" line without a jump; .day(iso) "Thu, Oct 29"; .left(min)
 * "3 hr 12 min"; event 'mv:zip' on window when the ZIP changes. window.mvShip() keeps its old shape for older readers.
 */
(function () {
  if (window.mvDelivery) return;
  const cfg = window.mvDeliveryCfg || {};
  const CUTOFF = Math.max(0, Math.min(23, parseInt(cfg.cutoff, 10) >= 0 ? parseInt(cfg.cutoff, 10) : 13));
  const MAKE_DAYS = 2;
  const HOLIDAYS = new Set([
    '2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25', '2026-06-19', '2026-07-03', '2026-09-07', '2026-10-12', '2026-11-11', '2026-11-26', '2026-11-27', '2026-12-25',
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18', '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25', '2027-11-26', '2027-12-24',
    '2027-12-31',
  ]);
  let EP = 'https://mvsport-studio-ai.cf.dpeyserapps.com';
  try {
    const o = sessionStorage.getItem('sv_ai_endpoint');
    if (o && localStorage.getItem('mv_internal') === '1' && /^https:\/\/mvsport-studio-ai(-[a-z]+)?\.cf\.dpeyserapps\.com$/.test(o)) EP = o;
  } catch (_) {}

  const iso = (d) => d.toISOString().slice(0, 10);
  const noon = (ymd) => new Date(`${ymd}T12:00:00Z`);
  const isBiz = (d) => { const w = d.getUTCDay(); return w !== 0 && w !== 6 && !HOLIDAYS.has(iso(d)); };
  const nextBiz = (d) => { const x = new Date(d); do x.setUTCDate(x.getUTCDate() + 1); while (!isBiz(x)); return x; };
  const addBiz = (d, n) => { let x = new Date(d); while (n-- > 0) x = nextBiz(x); return x; };
  // Today's date and minute of the day in Eastern Time (not the shopper's clock).
  function et(now) {
    const p = {};
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(now || new Date()).forEach((x) => { p[x.type] = x.value; });
    return { ymd: `${p.year}-${p.month}-${p.day}`, min: (+p.hour % 24) * 60 + +p.minute };
  }
  // The order's start day, and the minutes left before the cutoff (null after it, or on a day we do not print).
  function startNow(now) {
    const t = et(now), d = noon(t.ymd), left = CUTOFF * 60 - t.min;
    if (isBiz(d) && left > 0) return { start: d, leftMin: left };
    return { start: nextBiz(d), leftMin: null };
  }
  function dates(start, transit) {
    const ships = addBiz(start, MAKE_DAYS);
    return { start: iso(start), ships: iso(ships), arrives: transit == null ? null : iso(addBiz(ships, transit)) };
  }

  // ZIP and transit days.
  const ZIP_KEY = 'mv_zip';
  const clean = (z) => String(z || '').replace(/\D/g, '').slice(0, 5);
  const typedZip = () => { try { const z = clean(localStorage.getItem(ZIP_KEY)); return z.length === 5 ? z : null; } catch (_) { return null; } };
  const cache = {};
  function look(zip) {
    // Transit days per ZIP do not change within a day; keep them for the tab.
    const key = zip || 'here';
    if (cache[key]) return cache[key];
    try {
      const c = JSON.parse(sessionStorage.getItem('mv_tz') || '{}')[key];
      if (c && c.day === new Date().toDateString()) return (cache[key] = Promise.resolve(c.v));
    } catch (_) {}
    cache[key] = fetch(`${EP}/api/ship${zip ? `?zip=${zip}` : ''}`).then((r) => (r.ok ? r.json() : null)).then((s) => {
      if (s) try { const all = JSON.parse(sessionStorage.getItem('mv_tz') || '{}'); all[key] = { day: new Date().toDateString(), v: s }; sessionStorage.setItem('mv_tz', JSON.stringify(all)); } catch (_) {}
      return s;
    }).catch(() => { delete cache[key]; return null; });
    return cache[key];
  }
  // {zip, typed, known, transitDays, city, region, raw}: the typed ZIP, else the Worker's location guess.
  async function where() {
    const z = typedZip();
    const s = await look(z);
    if (!s) return { zip: z, typed: !!z, known: false, transitDays: null, raw: null };
    const zip = z || (s.known && s.zip) || null;
    return { zip, typed: !!z, known: !!zip, transitDays: zip ? s.transitDays : null, city: z ? null : s.city, region: z ? null : s.region, raw: s };
  }

  async function get(now) {
    const w = await where(), s = startNow(now);
    return { ...w, ...dates(s.start, w.transitDays), leftMin: s.leftMin, cutoffHour: CUTOFF };
  }
  // Redesign 2026-10-28: a date to show when there is no typed ZIP and no location guess, so the tile is never empty.
  // Uses the Bay Shore ZIP's transit days; callers label it as an estimate. Not used for anything that charges or ships.
  const EST_ZIP = cfg.estimateZip || '11706';
  async function estimate(now) {
    const d = await get(now);
    if (d.arrives) return { ...d, estimated: !d.typed };
    const e = await look(EST_ZIP), s = startNow(now);
    const t = e && e.transitDays != null ? e.transitDays : 3;
    return { zip: null, typed: false, known: false, estimated: true, fallback: true, transitDays: t, city: null, region: null, raw: e, ...dates(s.start, t), leftMin: s.leftMin, cutoffHour: CUTOFF };
  }
  // A group's close day (YYYY-MM-DD, its local close date): it closes at 11:59 PM and is printed from the next business day.
  async function fromClose(ymd) {
    const w = await where();
    return { ...w, ...dates(nextBiz(noon(ymd)), w.transitDays), leftMin: null, cutoffHour: CUTOFF };
  }
  // "Arrives by" for a group before it closes (task shopmvsport-group-arrival-date, 2026-11-02). The store's promise is
  // "4 to 7 business days" to the door (home page, Cal 2026-11-02) = MAKE_DAYS + 2 to 5 transit days. With a ZIP (typed or
  // guessed) the date is that ZIP's; with none it is the far end of the promise, so "arrives by" always holds.
  const PROMISE_MAX = 7;
  const closeDates = (ymd, w) => {
    const t = w && w.transitDays != null ? w.transitDays : PROMISE_MAX - MAKE_DAYS;
    return { ...(w || {}), estimated: !(w && w.transitDays != null), close: ymd, ...dates(nextBiz(noon(ymd)), t), leftMin: null, cutoffHour: CUTOFF };
  };
  async function closeEstimate(ymd) { return closeDates(ymd, await where()); }
  // The same, now, from this tab's cache only: null when the ZIP's transit days are not cached yet.
  function closeEstimateNow(ymd) {
    try {
      const z = typedZip(), c = JSON.parse(sessionStorage.getItem('mv_tz') || '{}')[z || 'here'];
      if (!c || c.day !== new Date().toDateString()) return null;
      const s = c.v, zip = z || (s.known && s.zip) || null;
      return closeDates(ymd, { zip, typed: !!z, known: !!zip, transitDays: zip ? s.transitDays : null });
    } catch (_) { return null; }
  }
  // Fills node with words(d) for close day ymd() ("Close by Fri, Nov 6 → arrives by Thu, Nov 12"), again when the ZIP
  // changes and whenever node.mvRepaint() is called. No jump: the line keeps its space (visibility only) until the date
  // is known, and from the tab's cache it is there on the first paint.
  function paintClose(node, ymd, words) {
    if (!node) return;
    let n = 0;
    const put = (d) => { node.textContent = words(d); node.style.visibility = ''; };
    const run = () => {
      const v = ymd(), k = ++n;
      if (!v) { node.textContent = ''; return; }
      const now = closeEstimateNow(v);
      if (now) put(now);
      else { node.textContent = words(closeDates(v, null)); node.style.visibility = 'hidden'; }
      closeEstimate(v).then((d) => { if (k === n) put(d); }).catch(() => { if (k === n) node.style.visibility = ''; });
    };
    node.mvRepaint = run;
    if (!node._mvz) { node._mvz = true; addEventListener('mv:zip', () => { if (node.isConnected) run(); }); }
    run();
  }
  function setZip(z, from) {
    const zip = clean(z);
    if (zip.length !== 5 || zip === typedZip()) return false;
    try { localStorage.setItem(ZIP_KEY, zip); } catch (_) {}
    if (window.mvTrack) window.mvTrack('delivery_zip_changed', { from: from || 'unknown', zip3: zip.slice(0, 3) });
    dispatchEvent(new CustomEvent('mv:zip', { detail: { zip, from } }));
    return true;
  }
  const day = (v) => { try { return new Date(`${v}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch (_) { return v; } };
  const left = (m) => { const h = Math.floor(m / 60), n = m % 60; return h ? `${h} hr${n ? ` ${n} min` : ''}` : `${Math.max(1, n)} min`; };
  // "Order within 3 hr 12 min, get it by" or "Order today, get it by" (before the cutoff on a business day, or not).
  const lead = (d) => (d.leftMin ? `Order within ${left(d.leftMin)}, get it by` : 'Order today, get it by');

  window.mvDelivery = { get, estimate, fromClose, closeEstimate, closeEstimateNow, paintClose, promiseMax: PROMISE_MAX, setZip, typedZip, day, left, lead, cutoffHour: CUTOFF, startNow, dates };

  // The old reader (snippets/ship-date.liquid before part 3, the Studio's loadShip, the cart, the footer): the Worker's
  // answer with arrivesBy and shipBy replaced by the dates above, and known/zip from the ZIP above.
  window.mvShipDay = day;
  window.mvShip = function () {
    return get().then((d) => (d.raw ? { ...d.raw, known: d.known, zip: d.zip, city: d.city || null, region: d.region || null, transitDays: d.transitDays, shipBy: d.ships, arrivesBy: d.arrives } : null)).catch(() => null);
  };
})();
